<template>
  <article
    class="message-card border rounded bg-body-emphasis p-3"
    :data-message-id="message.id"
  >
    <header class="d-flex flex-wrap align-items-center gap-2 mb-2">
      <strong>{{
        message.role === "user"
          ? "User"
          : message.role === "system"
            ? "System"
            : "Assistant"
      }}</strong>
      <span
        v-if="message.uploads.length"
        class="badge bg-secondary-subtle text-secondary-emphasis"
        >Document</span
      >
      <span v-if="message.step" class="badge" :class="stepClass"
        >{{ stepPill(message.step)
        }}{{ extractionPhase ? ` · phase ${extractionPhase}` : "" }}</span
      >
      <span
        v-if="message.is_fallback_reply"
        class="badge bg-secondary-subtle text-secondary-emphasis"
        >Fallback · no model call</span
      >
      <code v-if="message.ai_model_name" class="model-label">{{
        message.ai_model_name
      }}</code>
      <span class="small text-muted ms-auto"
        >{{ new Date(message.created_at).toLocaleTimeString() }} · msg
        {{ message.id }}</span
      >
    </header>
    <div v-if="message.uploads.length" class="d-flex flex-column gap-2 mb-3">
      <div
        v-for="upload in message.uploads"
        :key="upload.id"
        class="file-chip border rounded p-2 d-flex align-items-center gap-2"
      >
        <div class="min-width-0 flex-grow-1">
          <strong class="d-block text-break small">{{
            upload.display_name
          }}</strong
          ><small class="text-muted"
            >{{ upload.extension?.toUpperCase() }} ·
            {{ message.content.length.toLocaleString() }} chars extracted ·
            upload {{ upload.id }}</small
          ><span
            v-if="upload.deleted"
            class="badge bg-secondary-subtle text-secondary-emphasis ms-1"
            >Archived</span
          >
        </div>
        <button
          class="btn btn-sm btn-outline-primary flex-shrink-0"
          :disabled="downloading.includes(upload.id)"
          @click="download(upload)"
        >
          {{ downloading.includes(upload.id) ? "Downloading…" : "Download" }}
        </button>
      </div>
    </div>
    <template v-if="message.role === 'user'">
      <AiTextDisclosureComponent
        v-if="message.uploads.length"
        label="Extracted text"
        :text="message.content"
        :expanded="expandDocument"
      />
      <p v-else class="message-text mb-0">{{ message.content }}</p>
    </template>
    <template v-else-if="message.step === 'relevance_check'">
      <div class="d-flex gap-2 align-items-center mb-2">
        <span
          class="badge"
          :class="
            isRejected
              ? 'bg-danger-subtle text-danger-emphasis'
              : 'bg-success-subtle text-success-emphasis'
          "
          >{{ message.structured_output?.classification }}</span
        ><span v-if="confidence" class="small"
          >confidence <strong>{{ confidence }}</strong></span
        >
      </div>
      <p v-if="reasoning" class="message-text small mb-2">{{ reasoning }}</p>
      <div
        v-if="isRejected"
        class="rounded border border-danger-subtle bg-danger-subtle text-danger-emphasis p-2 mb-2"
        role="note"
      >
        Document rejected in this turn
      </div>
      <AiStructuredOutputComponent
        :value="message.structured_output"
        :chars="message.structured_output_chars"
        :expanded="false"
      />
      <AiTextDisclosureComponent
        v-if="message.content"
        label="Raw content"
        :text="message.content"
      />
    </template>
    <template v-else-if="message.step === 'reply'">
      <p class="message-text mb-3">{{ message.content }}</p>
      <div
        class="d-flex flex-wrap align-items-center gap-2 border-top pt-2 small"
      >
        <span
          class="badge"
          :class="
            message.draft_complete === true
              ? 'bg-success-subtle text-success-emphasis'
              : message.draft_complete === false
                ? 'bg-warning-subtle text-warning-emphasis'
                : 'bg-secondary-subtle text-secondary-emphasis'
          "
          >{{
            message.draft_complete === null
              ? "Draft"
              : message.draft_complete
                ? "Draft complete"
                : "Draft incomplete"
          }}</span
        >
        <span v-if="message.draft_summary" class="text-muted">{{
          message.draft_summary
        }}</span>
      </div>
      <AiStructuredOutputComponent
        label="Draft JSON"
        :value="message.structured_output?.chat_draft"
        :chars="draftChars"
        :expanded="false"
      />
    </template>
    <template v-else-if="message.step === 'rejection'">
      <div
        class="rounded border border-danger-subtle bg-danger-subtle text-danger-emphasis p-2 mb-2"
        role="note"
      >
        Turn rejected: nothing from it reached the draft
      </div>
      <p class="message-text mb-2">{{ message.content }}</p>
      <AiStructuredOutputComponent
        label="Correction"
        :value="message.structured_output?.chat_correction"
        :chars="message.structured_output_chars"
        :expanded="false"
      />
    </template>
    <template
      v-else-if="message.step === 'extraction' || message.step === 'merge'"
    >
      <AiDraftChangesComponent
        v-if="message.step === 'merge'"
        :changes="message.draft_changes"
        :total="message.draft_change_count"
      />
      <AiStructuredOutputComponent
        :value="message.structured_output"
        :chars="message.structured_output_chars"
        :expanded="message.step === 'merge' ? false : undefined"
      />
      <AiTextDisclosureComponent
        v-if="message.content"
        label="Raw content"
        :text="message.content"
      />
    </template>
    <template v-else>
      <span
        v-if="message.role === 'assistant' && !message.structured_output"
        class="badge bg-secondary-subtle text-secondary-emphasis mb-2"
        >No structured output</span
      >
      <AiTextDisclosureComponent
        v-if="message.content"
        label="Recorded content"
        :text="message.content"
        :expanded="true"
      />
      <AiStructuredOutputComponent
        v-if="message.structured_output"
        :value="message.structured_output"
        :chars="message.structured_output_chars"
      />
    </template>
    <AiTextDisclosureComponent
      v-if="message.system_prompt"
      class="mt-2"
      label="System prompt"
      :text="message.system_prompt"
    />
  </article>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { downloadUpload } from "./aiConversationsApi";
import type {
  AiMessageView,
  AiStep,
  AiUploadView,
} from "./aiConversationsTypes";
import { stepPill } from "./aiConversationsPresenters";

const STEP_CLASSES: Partial<Record<AiStep, string>> = {
  relevance_check: "bg-info-subtle text-info-emphasis",
  rejection: "bg-danger-subtle text-danger-emphasis",
  unclassified: "bg-secondary-subtle text-secondary-emphasis",
};
import AiStructuredOutputComponent from "./AiStructuredOutputComponent.vue";
import AiDraftChangesComponent from "./AiDraftChangesComponent.vue";
import AiTextDisclosureComponent from "./AiTextDisclosureComponent.vue";
const props = withDefaults(
  defineProps<{
    message: AiMessageView;
    expandDocument?: boolean;
    extractionPhase?: number;
  }>(),
  { expandDocument: false, extractionPhase: undefined }
);
const downloading = ref<number[]>([]);
const { globalErrorHandler } = useGlobalHandlers();
const isRejected = computed(
  () =>
    props.message.step === "relevance_check" &&
    props.message.structured_output?.classification === "IRRELEVANT"
);
const confidence = computed(() => {
  const value = props.message.structured_output?.confidence;
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : null;
});
const reasoning = computed(() => {
  const value = props.message.structured_output?.reasoning;
  return typeof value === "string" ? value : null;
});
const draftChars = computed(
  () => JSON.stringify(props.message.structured_output?.chat_draft ?? {}).length
);
const stepClass = computed(
  () =>
    STEP_CLASSES[props.message.step ?? "unclassified"] ??
    "bg-success-subtle text-success-emphasis"
);
async function download(upload: AiUploadView) {
  if (downloading.value.includes(upload.id)) return;
  downloading.value = [...downloading.value, upload.id];
  try {
    await downloadUpload(upload);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    downloading.value = downloading.value.filter((id) => id !== upload.id);
  }
}
</script>
<style scoped lang="scss">
.message-card {
  min-width: 0;
  font-size: 0.875rem;
}
.message-text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.model-label {
  background: var(--phoenix-secondary-bg);
  color: var(--phoenix-secondary-color);
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
  font-size: 0.75rem;
  overflow-wrap: anywhere;
}
.file-chip {
  background: var(--phoenix-body-bg);
}
.min-width-0 {
  min-width: 0;
}
</style>
